import { z } from 'zod';

export const ProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  api_key: z.string(),
  allowed_origins: z.array(z.string()),
  ip_truncate: z.boolean(),
  created_at: z.string(),
  events_24h: z.number().default(0),
  sessions_24h: z.number().default(0),
  last_event_at: z.string().nullish(),
});
export type Project = z.infer<typeof ProjectSchema>;

export const ReleaseSchema = z.object({
  version: z.string(),
  tag: z.string().nullish(),
  deployed_at: z.string(),
  sourcemap_at: z.string().nullish(),
});
export type Release = z.infer<typeof ReleaseSchema>;

export const ErrorEventSchema = z.object({
  time: z.string(),
  event_id: z.string().uuid(),
  session_id: z.string().uuid().nullish(),
  user_id: z.string().nullish(),
  kind: z.enum(['runtime', 'react', 'promise', 'network']),
  message: z.string(),
  url: z.string().nullish(),
  route: z.string().nullish(),
  ui_version: z.string(),
  release_tag: z.string().nullish(),
  http_status: z.number().nullish(),
  stack_top: z.string().nullish(),
  fingerprint: z.string(),
  // Heavy fields — only present on the detail endpoint (omitted by list).
  stack: z.string().nullish(),
  breadcrumbs: z.array(z.unknown()).nullish(),
  context: z.record(z.string(), z.unknown()).nullish(),
});
export type ErrorEvent = z.infer<typeof ErrorEventSchema>;

export const ErrorsPageSchema = z.object({
  items: z.array(ErrorEventSchema),
  next_cursor: z.string().optional(),
});

export const ErrorDetailSchema = z.object({
  fingerprint: z.string(),
  first_seen: z.string(),
  last_seen: z.string(),
  event_count: z.number(),
  recent_events: z.array(ErrorEventSchema),
});
export type ErrorDetail = z.infer<typeof ErrorDetailSchema>;

export const StatsBucketSchema = z.object({
  bucket: z.string(),
  count: z.number(),
  sessions: z.number(),
});
export type StatsBucket = z.infer<typeof StatsBucketSchema>;

export const PerformanceBucketSchema = z.object({
  bucket: z.string(),
  metric: z.string(),
  p50: z.number(),
  p75: z.number(),
  p95: z.number(),
  count: z.number(),
});
export type PerformanceBucket = z.infer<typeof PerformanceBucketSchema>;

export const SlowRowSchema = z.object({
  route:     z.string(),
  method:    z.string(),
  count:     z.number(),
  p50_ms:    z.number(),
  p95_ms:    z.number(),
  max_ms:    z.number(),
  worst_url: z.string().optional(),
});
export type SlowRow = z.infer<typeof SlowRowSchema>;

export const SymbolicateFrameSchema = z.object({
  original: z.string(),
  file: z.string().optional(),
  line: z.number().optional(),
  col:  z.number().optional(),
  name: z.string().optional(),
});
export type SymbolicateFrame = z.infer<typeof SymbolicateFrameSchema>;

export const SystemStatsSchema = z.object({
  started_at:       z.string(),
  uptime_sec:       z.number(),
  goroutines:       z.number(),
  heap_alloc:       z.number(),
  heap_sys:         z.number(),
  process_rss:      z.number(),
  mem_total:        z.number(),
  mem_available:    z.number(),
  mem_used:         z.number(),
  disk_total:       z.number(),
  disk_free:        z.number(),
  disk_used:        z.number(),
  db_size:          z.number(),
  wal_size:         z.number(),
  sourcemaps_size:  z.number(),
  events_enqueued:  z.number(),
  events_dropped:   z.number(),
  events_written:   z.number(),
  events_failed:    z.number(),
  live_subscribers: z.number(),
  live_delivered:   z.number(),
  live_dropped:     z.number(),
});
export type SystemStats = z.infer<typeof SystemStatsSchema>;

export const UserRowSchema = z.object({
  user_id:     z.string(),
  errors:      z.number(),
  sessions:    z.number(),
  ui_versions: z.array(z.string()),
  first_seen:  z.string(),
  last_seen:   z.string(),
});
export type UserRow = z.infer<typeof UserRowSchema>;

export const UsersPageSchema = z.object({
  items: z.array(UserRowSchema),
  next_cursor: z.string().optional(),
});
export type UsersPage = z.infer<typeof UsersPageSchema>;

export const SessionEventsPageSchema = z.object({
  items: z.array(ErrorEventSchema),
  next_cursor: z.string().optional(),
});
export type SessionEventsPage = z.infer<typeof SessionEventsPageSchema>;

export const IssueStateSchema = z.object({
  fingerprint:  z.string(),
  state:        z.enum(['open', 'resolved', 'ignored', 'regressed']),
  resolved_at:  z.string().nullish(),
  resolved_in:  z.string().nullish(),
  regressed_at: z.string().nullish(),
  note:         z.string().nullish(),
  updated_at:   z.string(),
});
export type IssueState = z.infer<typeof IssueStateSchema>;

export const ActiveSessionSchema = z.object({
  session_id:  z.string().uuid(),
  user_id:     z.string().nullish(),
  ui_version:  z.string(),
  device_type: z.string(),
  os:          z.string().nullish(),
  browser:     z.string().nullish(),
  browser_ver: z.string().nullish(),
  started_at:  z.string(),
  last_active: z.string(),
  errors_5m:   z.number(),
});
export type ActiveSession = z.infer<typeof ActiveSessionSchema>;

export const SessionDetailSchema = z.object({
  time: z.string(),
  session_id: z.string().uuid(),
  user_id: z.string().nullish(),
  ui_version: z.string(),
  device_type: z.string(),
  os: z.string().nullish(),
  browser: z.string().nullish(),
  browser_ver: z.string().nullish(),
  ip: z.string().nullish(),
  events: z.array(ErrorEventSchema),
});
export type SessionDetail = z.infer<typeof SessionDetailSchema>;
